import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';

/**
 * Focus Trap Directive for WCAG 2.1 Compliance (2.4.3 Focus Order)
 * Traps keyboard focus within a modal dialog to prevent users from tabbing to background content.
 * This is essential for screen reader users and keyboard navigation accessibility.
 */
@Directive({
  selector: '[appFocusTrap]',
  standalone: true,
})
export class FocusTrapDirective implements OnInit, OnDestroy {
  private focusableElements: HTMLElement[] = [];
  private firstFocusableElement: HTMLElement | null = null;
  private lastFocusableElement: HTMLElement | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    // Store the currently focused element to restore focus later
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    // Find all focusable elements within the dialog
    this.updateFocusableElements();

    // Focus the first element
    setTimeout(() => {
      if (this.firstFocusableElement) {
        this.firstFocusableElement.focus();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    // Restore focus to the previously focused element when dialog closes
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }

    // Update focusable elements in case the DOM changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    // Handle Shift+Tab
    if (event.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement?.focus();
      }
    } else {
      // Handle Tab
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement?.focus();
      }
    }
  }

  private updateFocusableElements(): void {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    this.focusableElements = Array.from(
      this.el.nativeElement.querySelectorAll<HTMLElement>(focusableSelectors),
    ).filter((el) => {
      // Only include visible elements
      return el.offsetParent !== null;
    });

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1] || null;
  }
}
